import { useState, useRef, useEffect, createContext, useContext } from 'react';
import { differenceInSeconds } from 'date-fns';

import { Sites } from '@labradorsports/constants';
import { useSite } from '@labradorsports/utils';

import { getPlaybackSpeedSettings } from '../utils/play.js';

import { usePlayEditor } from './playEditor.js';

export interface PlaybackValue {
    playing: boolean;
    setPlaying: (val: boolean) => void;
    animating: boolean;
    setAnimating: (val: boolean) => void;
    playbackSpeed: number;
    setPlaybackSpeed: (val: number) => void;
    playCurrentFrame: () => void;
    showingPlaybackScreen: string;
    setShowingPlaybackScreen: (val: string) => void;
}

interface Props {
    forceAnimation?: boolean;
    autoPlay?: boolean;
    autoLoop?: boolean;
    playbackRange?: { start: number; end: number };
    hideTitleScreen?: boolean;
}

export const PlaybackScreens = {
    TITLE: 'TITLE',
    REPLAY: 'REPLAY',
};

export const PlaybackContext = createContext<PlaybackValue>(null);

export const usePlayback = (): PlaybackValue => {
    return useContext(PlaybackContext);
};

export const PlaybackProvider: FC<Props> = ({
    children,
    forceAnimation,
    autoPlay,
    autoLoop,
    playbackRange,
    hideTitleScreen,
}) => {
    const { Config } = useSite();
    const {
        play,
        currentFrameIdx,
        setCurrentFrame,
        selectFrame,
        playData,
        currentFrame,
        fieldSetup,
        debugOptions,
        PlayConfig,
    } = usePlayEditor();
    const [playbackSpeed, setPlaybackSpeed] = useState(3);
    const [showingPlaybackScreen, setShowingPlaybackScreen] = useState(null);

    const integerFrameIdx = Math.floor(currentFrameIdx);
    const partialFramePercentage = currentFrameIdx - integerFrameIdx;
    const baseFrame = play?.frameSnapshots[integerFrameIdx];

    const timeouts = useRef<number[]>([]);
    const [animating, setAnimating] = useState(forceAnimation);
    const [playing, _setPlaying] = useState(false);

    // On change of frame index, either play the current frame, or just change frames
    useEffect(() => {
        if (play && !playing) {
            setCurrentFrame(
                partialFramePercentage > 0 ? baseFrame?.generateNextFrame(null, partialFramePercentage) : baseFrame
            );
        }
    }, [play, playing, currentFrameIdx]);

    useEffect(() => {
        if (fieldSetup) {
            selectFrame(0);
        }
    }, [fieldSetup]);

    const stopCurrentFrame = () => {
        if (timeouts.current.length > 0) {
            timeouts.current.forEach((id) => {
                clearTimeout(id);
            });

            timeouts.current = [];
        }
    };

    const setPlaying = (val: boolean) => {
        if (val) {
            setShowingPlaybackScreen(null);

            const atLastFrame =
                integerFrameIdx === play.frames.length - 1 ||
                (playbackRange != null && integerFrameIdx >= playbackRange.end);

            if (atLastFrame) {
                selectFrame(playbackRange?.start ?? 0);

                // Allow time to reset to first frame before starting playback
                setTimeout(() => {
                    setAnimating(true);
                    _setPlaying(true);
                }, 500);
            } else {
                if (currentFrameIdx !== integerFrameIdx) {
                    selectFrame(integerFrameIdx);
                }

                setAnimating(true);
                _setPlaying(true);
            }
        } else {
            _setPlaying(false);
            setAnimating(false);

            stopCurrentFrame();
        }
    };

    const playCurrentFrame = () => {
        if (!play) {
            return;
        }

        const currentFrame = play.frameSnapshots[currentFrameIdx];
        const playbackSpeedSettings = getPlaybackSpeedSettings(
            playbackSpeed,
            currentFrame?.duration,
            debugOptions,
            PlayConfig
        );

        const { frameDuration, totalDuration } = playbackSpeedSettings;
        const nextFrame = play.frameSnapshots[currentFrameIdx + 1];
        const nextIdx = currentFrameIdx + 1;
        const shouldLoopToStart =
            Boolean(playbackRange && autoLoop) &&
            (nextIdx > playbackRange.end || (nextIdx > playbackRange.start && !nextFrame));

        if (!currentFrame) {
            return;
        }

        if (!animating) {
            setAnimating(true);
        }

        // newFrame will be modified directly in every step of this frame,
        // and then copied into setCurrentFrame to update the screen
        const newFrame = currentFrame.copy();
        newFrame.players.forEach((plr) => {
            newFrame.updatePlayer(plr.id, {
                rotation: null,
            });
        });

        if (currentFrame.slides?.length > 0) {
            // Show the next frame role during animation
            currentFrame.slides.forEach((slide: any) => {
                const nextSlide = nextFrame?.slides?.find((sl: any) => sl.target === slide.target);

                if (nextSlide) {
                    newFrame.setRole(slide.target, nextSlide.role);
                }
            });
        }

        if (nextFrame?.assignments) {
            newFrame.assignments = nextFrame.assignments;
        }

        // set the starting state of the animation
        setCurrentFrame(newFrame.copy());

        timeouts.current.push(
            window.setTimeout(() => {
                newFrame.players
                    .map((plr) => nextFrame?.getPlayer(plr.id))
                    .filter((nextPlayer) => typeof nextPlayer?.rotation === 'number')
                    .forEach((nextPlayer) => {
                        newFrame.updatePlayer(nextPlayer.id, {
                            rotation: nextPlayer.rotation,
                        });
                    });

                // update player rotations halfway through frame animation
                setCurrentFrame(newFrame.copy());
            }, frameDuration / 2)
        );

        timeouts.current.push(
            window.setTimeout(() => {
                if (nextFrame) {
                    // set end state of animation
                    setCurrentFrame(nextFrame);
                } else if (!shouldLoopToStart) {
                    setPlaying(false);
                    setShowingPlaybackScreen(PlaybackScreens.REPLAY);
                }

                if (!playing) {
                    setAnimating(false);
                }

                if (shouldLoopToStart || nextFrame || Config.SiteConfig.Site === Sites.footballlab) {
                    // selecting the next frame will trigger that frame to animate
                    // Ensure that the currentFrame is updated before triggering that animation
                    timeouts.current.push(
                        window.setTimeout(() => {
                            if (shouldLoopToStart) {
                                selectFrame(playbackRange.start);
                            } else {
                                selectFrame(nextIdx);
                            }
                        }, 10)
                    );
                }
                // triggering this slightly before the animations timeout
                // prevents them from starting to replay before moving to the next frame
            }, totalDuration - 50)
        );
    };

    useEffect(() => {
        stopCurrentFrame();

        if (playing) {
            setShowingPlaybackScreen(null);
            playCurrentFrame();
        }
    }, [currentFrameIdx, playing]);

    // Update the current frame and stop playback whenever a new play is generated
    useEffect(() => {
        if (play) {
            setCurrentFrame(
                partialFramePercentage > 0 ? baseFrame.generateNextFrame(null, partialFramePercentage) : baseFrame
            );

            if (fieldSetup) {
                setShowingPlaybackScreen(null);
            }

            if (autoPlay) {
                setPlaying(true);
            } else {
                setPlaying(false);
            }
        }
    }, [play]);

    useEffect(() => {
        if (hideTitleScreen) {
            return;
        }

        // If play was just recently saved, don't show the title screen
        if (differenceInSeconds(new Date(), playData?.lastUpdated) < 10) {
            return;
        }

        setShowingPlaybackScreen(!play || play.fieldSetup ? null : PlaybackScreens.TITLE);
    }, [playData?.id, hideTitleScreen]);

    return (
        <PlaybackContext.Provider
            value={{
                playing,
                setPlaying,
                animating,
                setAnimating,
                playCurrentFrame,
                playbackSpeed,
                setPlaybackSpeed,
                showingPlaybackScreen,
                setShowingPlaybackScreen,
            }}
        >
            {children}
        </PlaybackContext.Provider>
    );
};
